import { Controller } from "@hotwired/stimulus"

// Backs Common::SpeakingClipPickerComponent. Two user actions:
//   - Search: hits GET /speaking_clips, lists burned phrases, click to pick.
//   - Suggest: hits POST /speaking_clips/suggest, runs PhrasePicker, fills slug.
//
// Slug value lives in a hidden input (`slug` target). The summary block
// re-renders on every pick/clear so the wizard form state stays in sync
// without a full page reload. CSRF token is pulled from the standard
// Rails meta tag.
const csrfToken = () => {
  const tag = document.querySelector('meta[name="csrf-token"]')
  return tag ? tag.content : ""
}

export default class extends Controller {
  static targets = ["slug", "summary", "panel", "searchInput", "results", "reviewedOnly", "languageFilter"]
  static values = {
    searchUrl: String,
    suggestUrl: String,
    characterId: Number,
    language: String,
    role: String,
    contextTags: String,
    wardrobe: String,
    orientation: String,
  }

  connect() {
    this.searchDebounceMs = 200
    this.searchTimer = null
    this.attachCharacterListener()
    this.attachRoleListener()
    // The component ships server-rendered as disabled when no character
    // was chosen at page load. Sync the enabled affordances now so the
    // initial DOM state is authoritative, and again whenever the
    // character changes live (see handleCharacterChange).
    this.syncEnabledState()
  }

  disconnect() {
    if (this.characterListener) {
      (this.characterEls || []).forEach((el) => el.removeEventListener("change", this.characterListener))
    }
    if (this.roleEl && this.roleListener) {
      this.roleEl.removeEventListener("change", this.roleListener)
    }
  }

  // When the picker lives inside a beat-sequence-editor row, the row
  // has a role <select> sibling that drives whether this beat is an
  // intro / outro / middle. Switching it should re-scope the picker
  // (clear current pick, refresh the open results panel).
  attachRoleListener() {
    const row = this.element.closest(".bse__row")
    if (!row) return
    this.roleEl = row.querySelector(".bse__role-select")
    if (!this.roleEl) return
    this.roleListener = (event) => this.handleRoleChange(event)
    this.roleEl.addEventListener("change", this.roleListener)
  }

  handleRoleChange(event) {
    // Beat-editor "role" semantic (intro/middle/outro/reaction) maps to
    // the PhrasePicker role: outro → farewell; everything else →
    // lesson_intro. Keep this in sync with the server-side default in
    // Common::BeatSequenceEditorComponent.
    const editorRole = event.target.value
    const phraseRole = editorRole === "outro" ? "farewell" : "lesson_intro"
    if (phraseRole === this.roleValue) return
    this.roleValue = phraseRole
    // Picked phrase no longer matches the new role context — clear it
    // so the user re-picks.
    this.applyPick("", "")
    if (this.hasPanelTarget && !this.panelTarget.hidden) {
      this.runSearch(this.hasSearchInputTarget ? this.searchInputTarget.value : "")
    }
  }

  // Watches the form's character control for live changes so the picker
  // re-scopes without a Save round-trip. Handles both select-based
  // pickers (taught_*) and radio-card pickers (word_of_day) keyed
  // off the canonical `video_config[config_data][<template>][character][character_id]`
  // input name.
  attachCharacterListener() {
    const form = this.element.closest("form")
    if (!form) return
    this.characterListener = (event) => this.handleCharacterChange(event)
    const sel = form.querySelector('select[name*="[character][character_id]"]')
    if (sel) {
      this.characterEls = [ sel ]
    } else {
      this.characterEls = Array.from(
        form.querySelectorAll('input[type="radio"][name*="[character][character_id]"]')
      )
    }
    this.characterEls.forEach((el) => el.addEventListener("change", this.characterListener))
  }

  handleCharacterChange(event) {
    const newCharacterId = parseInt(event.target.value || "0", 10)
    if (Number.isFinite(newCharacterId) && newCharacterId !== this.characterIdValue) {
      this.characterIdValue = newCharacterId
      // The previously-picked slug belonged to the OLD character and
      // won't exist on the new one — clear it so the user re-picks.
      this.applyPick("", "")
      if (this.hasPanelTarget) this.panelTarget.hidden = true
      // Picking a character live must ENABLE the Browse / Suggest
      // buttons the server rendered disabled (and vice-versa when the
      // character is cleared) — without a Save round-trip.
      this.syncEnabledState()
    }
  }

  // True once a real character is selected. The picker's search/suggest
  // endpoints resolve the character by id, so a positive id is a safe
  // proxy for the server's `picker_disabled?` (character present).
  get enabled() {
    return Number.isFinite(this.characterIdValue) && this.characterIdValue > 0
  }

  // The Browse + Suggest action buttons. Matched by data-action rather
  // than a wrapper class so the same lookup works in the real component
  // markup and in isolated controller tests.
  actionButtons() {
    return this.element.querySelectorAll(
      '[data-action*="speaking-clip-picker#toggleSearch"],[data-action*="speaking-clip-picker#suggest"]'
    )
  }

  // Reconciles the picker's disabled affordances (the `scp--disabled`
  // container class + each action button's `disabled` attribute) with
  // whether a character is currently selected. Also refreshes the
  // empty-state placeholder copy so it matches the server's two
  // messages exactly.
  syncEnabledState() {
    const enabled = this.enabled
    this.element.classList.toggle("scp--disabled", !enabled)
    this.actionButtons().forEach((btn) => btn.toggleAttribute("disabled", !enabled))
    if (this.hasSlugTarget && !this.slugTarget.value) this.renderPlaceholder()
  }

  renderPlaceholder() {
    if (!this.hasSummaryTarget) return
    this.summaryTarget.innerHTML = ""
    const placeholder = document.createElement("p")
    placeholder.className = "scp__placeholder"
    placeholder.textContent = this.enabled
      ? "No phrase chosen — auto-pick at render time."
      : "Pick a character first to enable this picker."
    this.summaryTarget.appendChild(placeholder)
  }

  // Browse button: always opens the panel and re-runs the search.
  // Doesn't toggle-hide (that surprised users) — `closePanel` covers
  // explicit dismissal.
  toggleSearch() {
    if (!this.enabled) return
    if (!this.hasPanelTarget) return
    this.panelTarget.hidden = false
    if (this.hasSearchInputTarget) this.searchInputTarget.focus()
    this.runSearch(this.hasSearchInputTarget ? this.searchInputTarget.value : "")
  }

  closePanel() {
    if (!this.hasPanelTarget) return
    this.panelTarget.hidden = true
  }

  search() {
    clearTimeout(this.searchTimer)
    const query = this.searchInputTarget.value
    this.searchTimer = setTimeout(() => this.runSearch(query), this.searchDebounceMs)
  }

  // Convenience: re-run the search with whatever's currently in the
  // search box. Used by event-driven re-fetches (toggle change, role
  // change) that don't have a query argument handy.
  runSearchFromInput() {
    this.runSearch(this.hasSearchInputTarget ? this.searchInputTarget.value : "")
  }

  // Search omits `language` so the server returns all language-tagged
  // burns. The result rows display a language badge so the user can
  // tell a Spanish burn apart from an English one for the same slug.
  // Suggest still sends the language since PhrasePicker needs it.
  async runSearch(query) {
    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("character_id", this.characterIdValue)
    url.searchParams.set("role", this.roleValue)
    if (query) url.searchParams.set("q", query)
    if (this.hasLanguageFilterTarget && this.languageFilterTarget.value) {
      url.searchParams.set("language", this.languageFilterTarget.value)
    }
    if (this.hasReviewedOnlyTarget && this.reviewedOnlyTarget.checked) {
      url.searchParams.set("reviewed_only", "1")
    }
    const res = await fetch(url.toString(), {
      headers: { "Accept": "application/json" },
      credentials: "same-origin",
    })
    if (!res.ok) {
      this.renderResults([])
      return
    }
    const data = await res.json()
    this.renderResults(data.results || [])
  }

  renderResults(rows) {
    if (!this.hasResultsTarget) return
    this.resultsTarget.innerHTML = ""
    if (rows.length === 0) {
      const empty = document.createElement("li")
      empty.className = "scp__empty"
      empty.textContent = "No burned phrases match."
      this.resultsTarget.appendChild(empty)
      return
    }
    rows.forEach((row) => {
      const li = document.createElement("li")
      li.className = "scp__result"
      li.setAttribute("data-slug", row.slug)
      li.setAttribute("data-text", row.text || "")

      // Left side: clickable region that picks this phrase. Wraps the
      // slug + language badge + text. Uses an internal data-action so
      // the play button (sibling) doesn't trigger a pick on click.
      const main = document.createElement("div")
      main.className = "scp__result-main"
      main.setAttribute("data-action", "click->speaking-clip-picker#pickResult")

      const slugRow = document.createElement("div")
      slugRow.className = "scp__result-slugrow"
      const slug = document.createElement("span")
      slug.className = "scp__result-slug"
      slug.textContent = row.slug
      slugRow.appendChild(slug)
      if (row.language) {
        const lang = document.createElement("span")
        lang.className = "scp__result-lang"
        lang.textContent = row.language.toUpperCase()
        slugRow.appendChild(lang)
      }

      const text = document.createElement("p")
      text.className = "scp__result-text"
      text.textContent = row.text || ""

      main.append(slugRow, text)
      li.appendChild(main)

      // Right side: a play button when the row has a media URL.
      if (row.media_url) {
        const playBtn = document.createElement("button")
        playBtn.type = "button"
        playBtn.className = "scp__play"
        playBtn.textContent = "▶"
        playBtn.title = "Play preview"
        playBtn.setAttribute("data-action", "click->speaking-clip-picker#togglePreview")
        playBtn.setAttribute("data-media-url", row.media_url)
        li.appendChild(playBtn)
      }

      this.resultsTarget.appendChild(li)
    })
  }

  togglePreview(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const url = btn.dataset.mediaUrl
    if (!url) return

    // Single shared <audio> element so only ONE preview plays at a
    // time. Toggle: clicking the same row's button stops; clicking
    // a different row's button switches.
    if (!this.previewAudio) {
      this.previewAudio = new Audio()
      this.previewAudio.addEventListener("ended", () => this.clearPlayingState())
    }

    if (this.currentlyPlayingBtn === btn) {
      this.previewAudio.pause()
      this.clearPlayingState()
      return
    }
    if (this.currentlyPlayingBtn) {
      this.currentlyPlayingBtn.textContent = "▶"
    }
    this.previewAudio.src = url
    this.previewAudio.play().catch(() => this.clearPlayingState())
    btn.textContent = "■"
    this.currentlyPlayingBtn = btn
  }

  clearPlayingState() {
    if (this.currentlyPlayingBtn) {
      this.currentlyPlayingBtn.textContent = "▶"
      this.currentlyPlayingBtn = null
    }
  }

  pickResult(event) {
    // `event.currentTarget` is the .scp__result-main wrapper; the slug
    // + text live on the parent .scp__result LI element.
    const li = event.currentTarget.closest(".scp__result")
    if (!li) return
    const slug = li.dataset.slug
    const text = li.dataset.text
    this.applyPick(slug, text)
    if (this.hasPanelTarget) this.panelTarget.hidden = true
  }

  async suggest() {
    if (!this.enabled) return
    let tags = []
    try { tags = JSON.parse(this.contextTagsValue || "[]") } catch (e) { tags = [] }
    const body = new URLSearchParams()
    body.set("character_id", this.characterIdValue)
    body.set("language", this.languageValue)
    body.set("role", this.roleValue)
    body.set("wardrobe_variant_slug", this.wardrobeValue)
    body.set("orientation", this.orientationValue)
    tags.forEach((t) => body.append("context_tags[]", t))
    const res = await fetch(this.suggestUrlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken(),
      },
      credentials: "same-origin",
      body: body.toString(),
    })
    if (!res.ok) return
    const data = await res.json()
    if (data.slug) {
      this.applyPick(data.slug, data.text || "")
    }
  }

  clear() {
    this.applyPick("", "")
  }

  applyPick(slug, text) {
    if (this.hasSlugTarget) {
      const changed = this.slugTarget.value !== slug
      this.slugTarget.value = slug
      // Writing `.value` programmatically dispatches NOTHING, so the
      // live-preview controller (remotion-preview: document-level
      // bubbling input/change listener filtered by field-name prefix)
      // never heard phrase picks. Re-announce the mutation with the
      // same bubbling events a hand-typed edit would produce — the
      // established idiom (audio_hook_picker, brand_kit_restore,
      // style_presets, …). Only when the value actually changed, so a
      // no-op clear (e.g. a role change with nothing picked) doesn't
      // trigger a spurious props refetch.
      if (changed) this.notifyFieldChanged(this.slugTarget)
    }
    if (!this.hasSummaryTarget) return
    if (slug) {
      this.summaryTarget.innerHTML = ""
      const slugEl = document.createElement("span")
      slugEl.className = "scp__slug"
      slugEl.textContent = slug
      const textEl = document.createElement("p")
      textEl.className = "scp__text"
      textEl.textContent = text || "[transcript not in library]"
      this.summaryTarget.append(slugEl, textEl)
    } else {
      this.renderPlaceholder()
    }
  }

  // Dispatches the bubbling events form-reactive listeners (notably the
  // remotion-preview live preview) key off. Both input AND change, matching
  // what a native edit on the field would emit.
  notifyFieldChanged(el) {
    el.dispatchEvent(new Event("input", { bubbles: true }))
    el.dispatchEvent(new Event("change", { bubbles: true }))
  }
}
